import React, { KeyboardEvent, ReactNode } from "react";
import styled from "styled-components";

import UserAvatar from "../avatar/UserAvatar";

/**
 * 横並びの「アバターアイコン」スタック表示。
 *
 * 設計意図:
 *  - 契約スレッド (`ThreadItem.tsx`) のアバタースタックと同じ視覚に揃え、
 *    プロジェクト内の「ユーザーを横並びで選ぶ」UI を 1 つに統合する。
 *  - 旧 `OfferContractorAvatars`（クレジット編集）と `CreatorRolesManager` の
 *    `AvatarCell` / `AvatarToggle`（正円リング選択）を置き換える受け皿。
 *  - 詳細表示（表示名・ロール・操作）はコンポーネントが持たない。呼び出し側が
 *    選択結果を別位置に独自レイアウトで描画する。
 *
 * 選択モード:
 *  - `selectedId`  単一選択（クレジット編集）。`radiogroup` / `radio`。
 *  - `selectedIds` 複数選択（CreatorRolesManager の toggle）。`button` + `aria-pressed`。
 *  - どちらも未指定なら表示のみ。
 *
 * 視覚仕様:
 *  - スーパー楕円は `Avatar` 側の既存実装を流用。
 *  - ホバー / 選択時の青 outline は `Avatar` の `selectable` / `selected` を使う
 *    （= グローバルメニューのアバター編集ホバーと同じビジュアル）。
 *  - 重なりは "左前面"。`overlap` 既定値 -6px。
 *  - グループ境界 (`groupBoundaries` に含まれる id) の前で +4px 開ける
 *    （契約スレッドの CLIENT/PRODUCER 境界と同じ挙動）。
 *  - オーバーフローは上限なし。横スクロール (`overflow-x: auto`) で全件見せる。
 */
export interface AvatarStackItem {
  /** 選択キー。`onSelect` のコールバック引数として返る。 */
  id: string | number;
  /** UserAvatar 経由で Redux userDirectory から解決する場合に渡す。 */
  userId?: number | string | null;
  /** 呼び出し側で既に持っているフォールバック avatar URL。 */
  avatar?: string | null;
  /** 表示名（ツールチップとフォールバックアイコンの色決定に使う）。 */
  displayName?: string | null;
}

export interface AvatarStackProps {
  items: AvatarStackItem[];
  /** アバターサイズ（px）。既定 40。 */
  size?: number;
  /**
   * 横の間隔 (px)。正値で隙間、0 で隣接、負値で左前面の重ね表示。
   * 既定 8（並列表示）。契約スレッドのようなスタック表示で重ねたい場合は
   * 呼び出し側で `-6` 等の負値を渡す。
   */
  overlap?: number;
  /** 単一選択時の選択中 id。 */
  selectedId?: string | number | null;
  /** 複数選択時の選択中 id 配列。`selectedId` と排他。 */
  selectedIds?: Array<string | number>;
  /**
   * クリック / Enter / Space で発火。未指定なら表示専用。
   * 単一選択モードでは同じ id を再選択する場合も呼ばれる（解除は呼び出し側の責務）。
   */
  onSelect?: (id: string | number, item: AvatarStackItem) => void;
  /** 個別ボタンを無効化する id 配列（連打抑止など）。 */
  disabledIds?: Array<string | number>;
  /**
   * 指定 id の前で +4px の隙間を空ける。契約スレッドの CLIENT/PRODUCER 境界
   * のようなグループ区切り用。
   */
  groupBoundaries?: Array<string | number>;
  /** ホバー時のツールチップに display_name を出すか。既定 true。 */
  showTooltip?: boolean;
  /** スタックの末尾に挿入する任意要素（無限スクロールの sentinel 等）。 */
  endSlot?: ReactNode;
  className?: string;
  /** スタック全体に付与する aria-label。 */
  ariaLabel?: string;
}

const StackRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  /* outline / box-shadow が縦横に溢れるため、padding で表示領域を確保する。
   * 縦は影が下方向に出るぶん多めに。 */
  overflow-x: auto;
  overflow-y: visible;
  scrollbar-width: thin;
  padding: 8px 12px;
  scroll-padding: 12px;
`;

const Slot = styled.button.attrs({ type: "button" })<{
  $overlap: number;
  $groupBoundary: boolean;
  $z: number;
  $clickable: boolean;
}>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0;
  background: none;
  border: none;
  flex-shrink: 0;
  cursor: ${({ $clickable, disabled }) =>
    disabled ? "progress" : $clickable ? "pointer" : "default"};
  z-index: ${({ $z }) => $z};

  margin-left: ${({ $overlap, $groupBoundary }) =>
    $groupBoundary ? "4px" : `${$overlap}px`};

  &:first-child {
    margin-left: 0;
  }

  &:disabled {
    opacity: 0.6;
  }

  &:focus {
    outline: none;
  }
  &:focus-visible {
    outline: 2px solid var(--soremo-blue);
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

const EndSlotWrapper = styled.div`
  flex-shrink: 0;
  margin-left: 8px;
  display: inline-flex;
  align-items: center;
`;

const AvatarStack: React.FC<AvatarStackProps> = ({
  items,
  size = 40,
  overlap = 8,
  selectedId = null,
  selectedIds,
  onSelect,
  disabledIds,
  groupBoundaries,
  showTooltip = true,
  endSlot,
  className,
  ariaLabel,
}) => {
  const isSelectable = !!onSelect;
  const isMultiSelect = !!selectedIds;
  const total = items.length;
  const groupBoundarySet = new Set(groupBoundaries ?? []);
  const selectedSet = new Set<string | number>(selectedIds ?? []);
  const disabledSet = new Set<string | number>(disabledIds ?? []);

  const handleKeyDown = (
    e: KeyboardEvent<HTMLButtonElement>,
    item: AvatarStackItem,
  ) => {
    if (!onSelect) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(item.id, item);
    }
  };

  return (
    <StackRow
      className={className}
      role={isSelectable && !isMultiSelect ? "radiogroup" : undefined}
      aria-label={ariaLabel}
    >
      {items.map((item, index) => {
        const isSelected = isMultiSelect
          ? selectedSet.has(item.id)
          : selectedId !== null && selectedId === item.id;
        const isDisabled = disabledSet.has(item.id);
        const isGroupBoundary = index > 0 && groupBoundarySet.has(item.id);
        return (
          <Slot
            key={String(item.id)}
            $overlap={overlap}
            $groupBoundary={isGroupBoundary}
            $z={total - index}
            $clickable={isSelectable}
            role={
              isSelectable && !isMultiSelect
                ? "radio"
                : isSelectable
                  ? "button"
                  : undefined
            }
            aria-checked={
              isSelectable && !isMultiSelect ? isSelected : undefined
            }
            aria-pressed={isMultiSelect ? isSelected : undefined}
            tabIndex={isSelectable && !isDisabled ? 0 : -1}
            disabled={!isSelectable || isDisabled}
            onClick={
              onSelect && !isDisabled
                ? () => onSelect(item.id, item)
                : undefined
            }
            onKeyDown={(e) => !isDisabled && handleKeyDown(e, item)}
          >
            <UserAvatar
              userId={item.userId ?? undefined}
              avatar={item.avatar ?? undefined}
              displayName={item.displayName ?? undefined}
              size={size}
              tooltipText={
                showTooltip ? item.displayName ?? undefined : undefined
              }
              selectable={isSelectable && !isDisabled}
              selected={isSelected}
            />
          </Slot>
        );
      })}
      {endSlot && <EndSlotWrapper>{endSlot}</EndSlotWrapper>}
    </StackRow>
  );
};

export default AvatarStack;
